/**
 * Recipe Index Validator
 *
 * Validates and filters fetched recipe index JSON, ensuring only valid entries
 * and non-empty categories are returned. Never throws — always returns a safe structure.
 */

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 300;
const MAX_PREP_TIME_LENGTH = 50;
const MAX_COOK_TIME_LENGTH = 50;
const MAX_SLUG_LENGTH = 80;
const VALID_DIFFICULTIES = ['easy', 'medium', 'hard'];
const CATEGORY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Validates a single recipe entry from the index.
 * @param {unknown} entry - A recipe entry object
 * @returns {boolean} True if all required fields are present and valid
 */
export function isValidIndexEntry(entry) {
  if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
    return false;
  }

  const { title, category, description, difficulty, prepTime, cookTime, slug } = entry;

  // All fields must be strings
  if (typeof title !== 'string') return false;
  if (typeof category !== 'string') return false;
  if (typeof description !== 'string') return false;
  if (typeof difficulty !== 'string') return false;
  if (typeof prepTime !== 'string') return false;
  if (typeof cookTime !== 'string') return false;
  if (typeof slug !== 'string') return false;

  // Title: required, max 100 chars
  if (title.length === 0 || title.length > MAX_TITLE_LENGTH) return false;

  // Category: required, lowercase alphanumeric + hyphens
  if (!CATEGORY_PATTERN.test(category)) return false;

  // Description: required, max 300 chars
  if (description.length === 0 || description.length > MAX_DESCRIPTION_LENGTH) return false;

  // Difficulty: required, one of easy/medium/hard
  if (!VALID_DIFFICULTIES.includes(difficulty)) return false;

  // PrepTime: required, max 50 chars
  if (prepTime.length === 0 || prepTime.length > MAX_PREP_TIME_LENGTH) return false;

  // CookTime: required, max 50 chars
  if (cookTime.length === 0 || cookTime.length > MAX_COOK_TIME_LENGTH) return false;

  // Slug: required, max 80 chars, lowercase alphanumeric + hyphens
  if (slug.length === 0 || slug.length > MAX_SLUG_LENGTH) return false;
  if (!SLUG_PATTERN.test(slug)) return false;

  return true;
}

/**
 * Validates a recipe index object, filtering out invalid entries and empty categories.
 * @param {unknown} rawIndex - The parsed JSON from index.json
 * @returns {{categories: Array<{name: string, displayName: string, recipes: Array}>}}
 */
export function validateRecipeIndex(rawIndex) {
  const safeResult = { categories: [] };

  // rawIndex must be a non-null object (not array)
  if (rawIndex === null || typeof rawIndex !== 'object' || Array.isArray(rawIndex)) {
    return safeResult;
  }

  // Must have a categories array
  if (!Array.isArray(rawIndex.categories)) {
    return safeResult;
  }

  const validCategories = [];

  for (const category of rawIndex.categories) {
    // Each category must be a non-null object with name and recipes
    if (category === null || typeof category !== 'object' || Array.isArray(category)) {
      continue;
    }

    if (typeof category.name !== 'string' || !category.name) {
      continue;
    }

    if (!Array.isArray(category.recipes)) {
      continue;
    }

    // Filter recipes to only valid entries
    const validRecipes = category.recipes.filter(isValidIndexEntry);

    // Skip categories that end up with no valid recipes
    if (validRecipes.length === 0) {
      continue;
    }

    validCategories.push({
      name: category.name,
      displayName: category.displayName || category.name,
      description: typeof category.description === 'string' ? category.description : '',
      recipes: validRecipes,
    });
  }

  return { categories: validCategories };
}
